import { Capacitor } from "@capacitor/core";
import React from "react";
import { getGoogleOneTapClientId } from "../../../environment.js";
import { useAuth } from "../useAuth.js";
import {
  decodeCredentialEmail,
  loadGoogleIdentityServices,
  type GoogleAccountsId,
} from "./googleIdentityServices.js";

interface Options {
  /**
   * Called with the email of the account the visitor picked in the prompt.
   * The email is a login hint only; the credential itself is dropped here
   * and never leaves the browser.
   */
  onAccountPicked: (email: string) => void;
}

/**
 * Offers the visitor's Google account through Google One Tap.
 *
 * Active only for anonymous visitors on the web build with a configured
 * client id; everywhere else (and whenever the script is blocked, the browser
 * has no One Tap support, or the visitor dismisses the prompt) it does
 * nothing, so the page keeps working with its plain Google button.
 */
export function useGoogleOneTap({ onAccountPicked }: Options): void {
  const { isAuthenticated } = useAuth();
  // An empty string counts as "not configured", like a missing key.
  const clientId = getGoogleOneTapClientId() || undefined;
  const isEnabled =
    clientId !== undefined && !isAuthenticated && !Capacitor.isNativePlatform();

  // The prompt is initialized once; later renders must still reach the latest callback.
  const onAccountPickedRef = React.useRef(onAccountPicked);
  React.useEffect(() => {
    onAccountPickedRef.current = onAccountPicked;
  }, [onAccountPicked]);

  React.useEffect(() => {
    if (!isEnabled || clientId === undefined) {
      return;
    }

    let isActive = true;
    let accountsId: GoogleAccountsId | undefined;

    loadGoogleIdentityServices()
      .then(id => {
        if (!isActive) {
          return;
        }
        accountsId = id;
        id.initialize({
          client_id: clientId,
          callback: response => {
            const email = response.credential
              ? decodeCredentialEmail(response.credential)
              : undefined;
            if (email) {
              onAccountPickedRef.current(email);
            }
          },
        });
        id.prompt();
      })
      .catch(() => {
        // Blocked script, offline, or a browser without One Tap support: the
        // "Continue with Google" button is the fallback. Deliberately not
        // reported — ad blockers make this a routine, user-side outcome.
      });

    return () => {
      isActive = false;
      accountsId?.cancel();
    };
  }, [isEnabled, clientId]);
}
